import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createGlow, createSuccessBurst, createWinBurst } from '../utils/vfx';
import './Login.css';

const Login = () => {
  const [loginMode, setLoginMode] = useState('regular'); // 'regular' or 'student'
  const [guestLoading, setGuestLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    leagueCode: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { actions } = useUser();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const authService = (await import('../services/AuthService')).default;
      let response;
      
      if (loginMode === 'student') {
        // Student login (league code is required)
        if (!formData.leagueCode) {
          toast.error('League code is required for student login');
          setLoading(false);
          return;
        }
        
        response = await authService.studentLogin(
          formData.username,
          formData.password,
          formData.leagueCode,
          rememberMe
        );
      } else {
        // Regular login
        response = await authService.login(formData.email, formData.password, rememberMe);
      }
      
      if (response.token) {
        // Update user context
        actions.setUser(response.user);
        actions.setOnboarded(true);
        
        soundEffects.playSuccess();
        const loginButton = document.querySelector('.login-button');
        if (loginButton) {
          createSuccessBurst(loginButton);
        }
        setTimeout(() => {
          const container = document.querySelector('.login-container');
          if (container) {
            createWinBurst(container);
          }
        }, 300);
        
        toast.success('Login successful!');
        setTimeout(() => navigate('/dashboard'), 800);
      }
    } catch (error) {
      soundEffects.playError();
      toast.error(error.response?.data?.error || error.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    if (loginMode === 'student') {
      return formData.username && formData.password && formData.leagueCode;
    } else {
      return formData.email && formData.password;
    }
  };

  const handleContinueAsGuest = async (e) => {
    e.preventDefault();
    soundEffects.playClick();
    setGuestLoading(true);
    try {
      const authService = (await import('../services/AuthService')).default;
      const response = await authService.guestLogin({
        name: 'Guest User',
        ageRange: '18-24',
        financialGoal: 'investing',
        learningMode: 'facts'
      });
      if (response.token) {
        actions.setUser(response.user);
        actions.setOnboarded(true);
        authService.saveUserData({
          user: response.user,
          isOnboarded: true,
          preferences: { learningMode: 'facts', notifications: true, dailyFactTime: '08:00' },
          progress: { level: 1, xp: 0, streak: 0, badges: [], completedFacts: [], completedLessons: [] },
          portfolio: { balance: 10000, stocks: [], transactions: [] }
        });
        toast.success("You're in! Exploring as guest.");
        setTimeout(() => navigate('/dashboard'), 400);
      }
    } catch (error) {
      setGuestLoading(false);
      toast.error(error.response?.data?.error || 'Could not start guest session. Please try again.');
    }
  };

  return (
    <div className="login-container">
      {/* Stock Chart Background */}
      <div className="login-background">
        <svg className="login-stock-chart" viewBox="0 0 1200 800" preserveAspectRatio="none">
          {/* Multiple stock lines going up */}
          <polyline
            points="0,700 100,650 200,600 300,550 400,500 500,450 600,400 700,350 800,300 900,250 1000,200 1100,150 1200,100"
            fill="none"
            stroke="rgba(59, 130, 246, 0.3)"
            strokeWidth="3"
            className="login-stock-line"
          />
          <polyline
            points="0,750 150,700 250,650 350,600 450,550 550,500 650,450 750,400 850,350 950,300 1050,250 1150,200 1200,150"
            fill="none"
            stroke="rgba(37, 99, 235, 0.25)"
            strokeWidth="3"
            className="login-stock-line"
          />
          <polyline
            points="0,680 120,630 220,580 320,530 420,480 520,430 620,380 720,330 820,280 920,230 1020,180 1120,130 1200,80"
            fill="none"
            stroke="rgba(96, 165, 250, 0.2)"
            strokeWidth="2"
            className="login-stock-line"
          />
          <polyline
            points="0,720 130,670 230,620 330,570 430,520 530,470 630,420 730,370 830,320 930,270 1030,220 1130,170 1200,120"
            fill="none"
            stroke="rgba(59, 130, 246, 0.2)"
            strokeWidth="2"
            className="login-stock-line"
          />
        </svg>
      </div>
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/LogoGurt.jpg" alt="InvestNYou Logo" className="login-logo-img" />
          </div>
          <h1 className="login-title">Welcome Back!</h1>
          <p className="login-subtitle">Sign in to continue your financial journey</p>
        </div>

        {/* Login Mode Toggle */}
        <div className="login-mode-toggle" style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => {
              setLoginMode('regular');
              setFormData({ email: '', username: '', password: '', leagueCode: '' });
              soundEffects.playClick();
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '2px solid',
              borderColor: loginMode === 'regular' ? '#3b82f6' : '#e5e7eb',
              backgroundColor: loginMode === 'regular' ? '#3b82f6' : 'transparent',
              color: loginMode === 'regular' ? 'white' : '#6b7280',
              cursor: 'pointer',
              fontWeight: loginMode === 'regular' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            Regular Login
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMode('student');
              setFormData({ email: '', username: '', password: '', leagueCode: '' });
              soundEffects.playClick();
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '2px solid',
              borderColor: loginMode === 'student' ? '#3b82f6' : '#e5e7eb',
              backgroundColor: loginMode === 'student' ? '#3b82f6' : 'transparent',
              color: loginMode === 'student' ? 'white' : '#6b7280',
              cursor: 'pointer',
              fontWeight: loginMode === 'student' ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            Student Login
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {loginMode === 'regular' ? (
            <>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your email"
            />
          </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="leagueCode" className="form-label">
                  League Code <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  id="leagueCode"
                  name="leagueCode"
                  value={formData.leagueCode}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter league code"
                  style={{ textTransform: 'uppercase' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Your teacher will provide you with the league code for your class.
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter your username"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
              placeholder="Enter your password"
            />
          </div>

          <div className="form-group login-remember-me-group">
            <label className="login-remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="login-remember-me-checkbox"
              />
              <span className="login-remember-me-text">Remember me</span>
            </label>
          </div>

          <button
            type="submit"
            onClick={(e) => {
              if (!loading && isFormValid()) {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
              } else {
                soundEffects.playError();
              }
            }}
            onMouseEnter={(e) => {
              if (!loading && isFormValid()) {
                soundEffects.playHover();
                createGlow(e.currentTarget, '#3b82f6', 300);
              }
            }}
            disabled={loading || !isFormValid()}
            className={`login-button ${
              loading || !isFormValid()
                ? 'secondary'
                : 'primary'
            }`}
          >
            {loading ? (
              <div className="login-button-content">
                <div className="login-spinner"></div>
                Signing In...
              </div>
            ) : (
              'Sign In'
            )}
          </button>

          <div className="guest-button">
            <button
              type="button"
              onClick={handleContinueAsGuest}
              disabled={guestLoading}
              className="guest-button-link"
              style={{ background: 'none', border: '1px solid #d1d5db', cursor: guestLoading ? 'not-allowed' : 'pointer' }}
            >
              {guestLoading ? 'Entering...' : 'Continue as Guest'}
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p className="login-footer-text">
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e, '#3b82f6');
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="login-link"
            >
              Sign up here
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Login;
